import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '~/components/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { cn } from '~/lib/utils';

interface CustomPaginationProps {
  limit: number;
  limits?: number[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onPageChange: (page: string) => void;
  onLimitChange: (limit: string) => void;
}

const CustomPagination = ({
  limit,
  limits = [6, 12, 24, 36, 48, 60, 100],
  totalCount,
  totalPages,
  currentPage,
  onNextPage,
  onPrevPage,
  onPageChange,
  onLimitChange,
}: CustomPaginationProps) => {
  return (
    <div className="my-4 flex flex-col gap-y-4 md:flex-row md:justify-between">
      <div className="ms:min-w-[350px] flex flex-row items-center gap-2">
        <span className="whitespace-nowrap text-sm">Rows per page:</span>
        <Select value={limit.toString()} onValueChange={onLimitChange}>
          <SelectTrigger className="w-[70px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {limits.map((value, index) => (
              <SelectItem key={index} value={value.toString()} className="capitalize">
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="whitespace-nowrap text-sm">{`Showing ${currentPage === 1 ? 1 : currentPage * limit - limit}-${currentPage !== totalPages ? currentPage * limit : totalCount} of ${totalCount}`}</span>
      </div>
      <Pagination>
        <PaginationContent>
          <PaginationItem
            className={cn('cursor-pointer', currentPage === 1 && 'opacity-20')}
            onClick={currentPage !== 1 ? onPrevPage : undefined}
          >
            <PaginationPrevious />
          </PaginationItem>
          {currentPage - 1 > 1 && (
            <div className="hidden md:flex">
              <PaginationItem
                className={cn('cursor-pointer', currentPage === 1 && 'opacity-20')}
                onClick={() => onPageChange((1).toString())}
              >
                <PaginationLink className={cn('border hover:bg-white', currentPage === 1 && 'border-gray-500')}>
                  {1}
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            </div>
          )}
          {Array.from({ length: totalPages || 1 }).map((_, index) => {
            if (currentPage === index + 1 || (currentPage >= index && currentPage <= index + 2))
              return (
                <PaginationItem
                  key={index}
                  className={cn('cursor-pointer', currentPage === index + 1 && 'opacity-20')}
                  onClick={() => onPageChange((index + 1).toString())}
                >
                  <PaginationLink
                    className={cn('border hover:bg-white', currentPage === index + 1 && 'border-gray-500')}
                  >
                    {index + 1}
                  </PaginationLink>
                </PaginationItem>
              );
          })}
          {currentPage + 1 < totalPages && (
            <div className="hidden md:flex">
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
              <PaginationItem
                className={cn('cursor-pointer', currentPage === totalPages && 'opacity-20')}
                onClick={() => onPageChange(totalPages.toString())}
              >
                <PaginationLink
                  className={cn('border hover:bg-white', currentPage === totalPages && 'border-gray-500')}
                >
                  {totalPages}
                </PaginationLink>
              </PaginationItem>
            </div>
          )}
          <PaginationItem
            className={cn('cursor-pointer', (currentPage === totalPages || totalPages === 0) && 'opacity-20')}
            onClick={currentPage !== totalPages && totalPages !== 0 ? onNextPage : undefined}
          >
            <PaginationNext />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
      <div className="hidden md:block md:min-w-[350px]"></div>
    </div>
  );
};

export default CustomPagination;
